import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Briefcase, Shield, Warehouse, History, Truck as TruckIcon } from "lucide-react";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { salvageLabel, useBranding } from "../lib/company";
import { cn } from "../lib/utils";

/**
 * Global search widget — extracted Feb 2026 from Topbar so it can be
 * mounted anywhere. Fires `/api/search?q=…` on 250 ms debounce, shows a
 * grouped dropdown of Active Jobs / Impounds / Owned in Lieu / History /
 * Trucks. Enter falls back to `/history?q=…`. Broadcasts a
 * `global-search` window event so HistoryPage can stay in sync.
 *
 * Props:
 *   size = "md" (default) | "lg" — `lg` uses larger padding and text,
 *          suited to the tablet dispatcher slot where the search is the
 *          hero widget.
 *   testidPrefix = "topbar" | "dashboard" (default "topbar") — namespaces
 *          the search input testid so both mount points can be found by
 *          automated tests without collision.
 */
export default function GlobalSearch({ size = "md", testidPrefix = "topbar" }) {
  const branding = useBranding();
  const sname = branding?.salvage_entity_name || salvageLabel();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [resultsOpen, setResultsOpen] = useState(false);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setResultsOpen(false);
      window.dispatchEvent(new CustomEvent("global-search", { detail: { q: "" } }));
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(trimmed)}`);
        setResults(r.data);
        setResultsOpen(true);
      } catch {/* silent */}
      window.dispatchEvent(new CustomEvent("global-search", { detail: { q: trimmed } }));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!resultsOpen) return;
    const close = (e) => {
      if (!e.target.closest?.("[data-search-shell]")) setResultsOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [resultsOpen]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && q.trim()) {
      nav(`/history?q=${encodeURIComponent(q.trim())}`);
    }
  };

  const inputClasses = cn(
    "bg-slate-50 border-slate-200 focus-visible:ring-blue-500",
    size === "lg" ? "pl-11 h-12 text-base" : "pl-9",
  );
  const iconClasses = cn(
    "absolute top-1/2 -translate-y-1/2 text-slate-400",
    size === "lg" ? "left-3.5 h-5 w-5" : "left-3 h-4 w-4",
  );

  return (
    <div className="w-full relative" data-search-shell>
      <Search className={iconClasses} />
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => results && setResultsOpen(true)}
        placeholder={
          size === "lg"
            ? "Search by rego, job number, customer, address…"
            : `Search jobs, impounds, ${sname}, history, trucks…`
        }
        className={inputClasses}
        data-testid={`${testidPrefix}-search`}
      />
      {resultsOpen && results && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-50 max-h-[70vh] overflow-y-auto"
          data-testid={`${testidPrefix}-search-dropdown`}
        >
          {(() => {
            const groups = [
              { key: "active_jobs", label: "Active Jobs", icon: Briefcase, rows: results.active_jobs },
              { key: "impounds", label: "Impounds Current", icon: Shield, rows: results.impounds },
              { key: "nts", label: "Owned in Lieu", icon: Warehouse, rows: results.nts },
              { key: "history", label: "History", icon: History, rows: results.history },
              { key: "trucks", label: "Trucks", icon: TruckIcon, rows: results.trucks },
            ];
            const total = groups.reduce((s, g) => s + g.rows.length, 0);
            if (total === 0) {
              return (
                <div
                  className="px-3 py-3 text-sm text-slate-500 italic"
                  data-testid={`${testidPrefix}-search-empty`}
                >
                  No matches
                </div>
              );
            }
            return groups.filter((g) => g.rows.length).map((g) => (
              <div key={g.key} className="border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 flex items-center gap-1.5">
                  <g.icon className="h-3 w-3" /> {g.label}
                </div>
                {g.rows.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => {
                      setResultsOpen(false);
                      nav(`${r.link}?q=${encodeURIComponent(q)}`);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800/60"
                    data-testid={`${testidPrefix}-search-result-${r.type}-${r.id}`}
                  >
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{r.label || "—"}</div>
                    {r.sub && <div className="text-[11px] text-slate-500 truncate">{r.sub}</div>}
                  </button>
                ))}
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
}
